import { useEffect, useMemo, useState } from 'react';
import type { Branding } from '@helios/ui';
import intelisHeroImage from './intelis-hero.png';
import intelisLogoImage from './intelis-logo.png';
import ipcaHeroImage from './ipca-hero.png';

type Props = { branding: Branding };
type FeatureIcon = 'network' | 'people' | 'book' | 'chart' | 'shield';
type Feature = { title: string; body: string; icon: FeatureIcon };

export function HubIntelishealthLoginLeft({ branding }: Props) {
  const primaryColor = branding.theme?.primaryColor ?? '#2563EB';
  const azure = '#DBEAFE';
  const mint = '#D1FAE5';
  const ink = '#0F172A';
  const muted = '#475569';
  const [activeSlide, setActiveSlide] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const slides = useMemo(
    () => [
      {
        id: 'ipca',
        image: ipcaHeroImage,
        eyebrow: 'INTELIS & IPCA COLLABORATION',
        title: (
          <>
            <span>Better practice.</span>
            <br />
            <span style={{ color: primaryColor }}>Healthier tomorrows.</span>
          </>
        ),
        body: 'Everyday clinical tools, scientific knowledge, and intelligent insights designed to support doctors and strengthen patient care.',
        features: [
          {
            title: 'Smarter Practice',
            body: 'Digital tools that make everyday clinical work simpler and more connected.',
            icon: 'network' as FeatureIcon,
          },
          {
            title: 'Stronger Patient Relationships',
            body: 'Support continuity, follow-up, and better patient engagement beyond the consultation.',
            icon: 'people' as FeatureIcon,
          },
          {
            title: 'Latest Scientific Knowledge',
            body: 'Relevant clinical and scientific information available within the doctor workflow.',
            icon: 'book' as FeatureIcon,
          },
        ] satisfies Feature[],
      },
      {
        id: 'intelis',
        image: intelisHeroImage,
        eyebrow: 'AI-powered healthcare intelligence',
        title: (
          <>
            Intelligence designed
            <br />
            for the <span style={{ color: primaryColor }}>future</span> of
            <br />
            healthcare.
          </>
        ),
        body: 'Connecting workflows, operations, patient journeys, and AI-driven insights across modern healthcare networks.',
        features: [
          {
            title: 'Connected Care',
            body: 'Seamless collaboration across people and platforms.',
            icon: 'network' as FeatureIcon,
          },
          {
            title: 'Smart Insights',
            body: 'Data-driven decisions for better outcomes.',
            icon: 'chart' as FeatureIcon,
          },
          {
            title: 'Secure & Compliant',
            body: 'Enterprise-grade security you can trust.',
            icon: 'shield' as FeatureIcon,
          },
        ] satisfies Feature[],
      },
    ],
    [primaryColor],
  );

  const currentSlide = slides[activeSlide];

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion || hasInteracted || slides.length <= 1) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible')
        setActiveSlide((current) => (current + 1) % slides.length);
    }, 9000);
    return () => window.clearInterval(id);
  }, [hasInteracted, prefersReducedMotion, slides.length]);

  const renderIcon = (icon: FeatureIcon) => {
    if (icon === 'network')
      return (
        <svg width="25" height="25" viewBox="0 0 32 32" fill="none" aria-hidden>
          <circle cx="16" cy="8" r="4" stroke="currentColor" strokeWidth="2.2" />
          <circle cx="8" cy="24" r="4" stroke="currentColor" strokeWidth="2.2" />
          <circle cx="24" cy="24" r="4" stroke="currentColor" strokeWidth="2.2" />
          <path
            d="M14 11.5 9.8 20M18 11.5 22.2 20M12 24h8"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      );
    if (icon === 'people')
      return (
        <svg width="25" height="25" viewBox="0 0 32 32" fill="none" aria-hidden>
          <circle cx="12" cy="11" r="4" stroke="currentColor" strokeWidth="2.2" />
          <circle cx="22" cy="12" r="3.2" stroke="currentColor" strokeWidth="2.2" />
          <path
            d="M5.5 25c.7-5 3.3-7.5 6.5-7.5s5.8 2.5 6.5 7.5M18 19c1.2-1.2 2.6-1.8 4.2-1.8 2.6 0 4.4 2 4.9 5.8"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      );
    if (icon === 'book')
      return (
        <svg width="25" height="25" viewBox="0 0 32 32" fill="none" aria-hidden>
          <path
            d="M6 6.5h7c2 0 3 1 3 3v16c0-2-1.4-3-3.5-3H6v-16ZM26 6.5h-7c-2 0-3 1-3 3v16c0-2 1.4-3 3.5-3H26v-16Z"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinejoin="round"
          />
        </svg>
      );
    if (icon === 'chart')
      return (
        <svg width="25" height="25" viewBox="0 0 32 32" fill="none" aria-hidden>
          <path d="M6 25h20" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          <path
            d="M9 21v-5M16 21V10M23 21v-8"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
          <path
            d="m8 12 6-5 5 4 5-7"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    return (
      <svg width="25" height="25" viewBox="0 0 32 32" fill="none" aria-hidden>
        <path
          d="M16 4 25 8v7c0 6-3.8 10.5-9 13-5.2-2.5-9-7-9-13V8l9-4Z"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinejoin="round"
        />
        <path
          d="m11.5 16 3 3 6-7"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        backgroundImage: `linear-gradient(90deg, rgba(239,246,255,.98) 0%, rgba(239,246,255,.92) 31%, rgba(219,234,254,.34) 56%, rgba(219,234,254,0) 100%), radial-gradient(980px 620px at 16% 12%, ${azure}F0 0%, ${azure}88 38%, ${azure}00 76%), radial-gradient(920px 620px at 36% 88%, rgba(34,211,238,.20) 0%, rgba(34,211,238,.08) 36%, rgba(34,211,238,0) 72%), linear-gradient(160deg,#EFF6FF 0%,#F8FBFF 42%,#EAF6FF 100%)`,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      >
        <div
          key={currentSlide.id}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            backgroundImage: `url('${currentSlide.image}')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center right',
            filter: 'saturate(1.12) contrast(1.04)',
            transition: prefersReducedMotion ? 'none' : 'opacity 500ms ease',
            WebkitMaskImage:
              'linear-gradient(90deg,rgba(0,0,0,0) 0%,rgba(0,0,0,.52) 28%,rgba(0,0,0,.95) 48%,rgba(0,0,0,1) 100%)',
            maskImage:
              'linear-gradient(90deg,rgba(0,0,0,0) 0%,rgba(0,0,0,.52) 28%,rgba(0,0,0,.95) 48%,rgba(0,0,0,1) 100%)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            backgroundImage:
              'linear-gradient(90deg,rgba(239,246,255,.96) 0%,rgba(239,246,255,.88) 32%,rgba(219,234,254,.45) 52%,rgba(219,234,254,0) 74%)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            backgroundImage:
              'linear-gradient(180deg,rgba(239,246,255,.70) 0%,rgba(239,246,255,0) 22%,rgba(239,246,255,.72) 100%)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.28,
            filter: 'blur(70px)',
            backgroundColor: azure,
            width: '26rem',
            height: '26rem',
            top: '-8rem',
            left: '-8rem',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.18,
            filter: 'blur(70px)',
            backgroundColor: primaryColor,
            width: '20rem',
            height: '20rem',
            top: '18%',
            left: '18%',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderRadius: '9999px',
            opacity: 0.14,
            filter: 'blur(80px)',
            backgroundColor: mint,
            width: '600px',
            height: '600px',
            bottom: '-14rem',
            left: '16%',
          }}
        />
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 10,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1.35rem 4rem 1rem',
          }}
        >
          <img
            src={intelisLogoImage}
            alt="Intelis"
            style={{
              width: 'clamp(7.75rem,10vw,10.5rem)',
              maxHeight: '2.5rem',
              height: 'auto',
              objectFit: 'contain',
              objectPosition: 'left center',
            }}
          />
        </div>

        <div
          style={{
            padding: '2rem 4rem 3.25rem',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <div style={{ maxWidth: '44rem' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '.55rem',
                padding: '.5rem .9rem',
                borderRadius: '9999px',
                backgroundColor: `${primaryColor}0F`,
                color: primaryColor,
                fontSize: '.78rem',
                fontWeight: 800,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                marginBottom: '1.35rem',
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M12 3.5 13.35 8.25 18 9.6 13.35 10.95 12 15.5 10.65 10.95 6 9.6 10.65 8.25 12 3.5Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
              {currentSlide.eyebrow}
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: 'clamp(2.2rem,2.8vw,3.55rem)',
                lineHeight: 1.12,
                letterSpacing: '-.04em',
                color: ink,
                fontWeight: 760,
                maxWidth: '38rem',
              }}
            >
              {currentSlide.title}
            </h1>
            <p
              style={{
                margin: '1.35rem 0 0',
                fontSize: '1.05rem',
                lineHeight: 1.58,
                color: muted,
                maxWidth: '36rem',
              }}
            >
              {currentSlide.body}
            </p>
            <div
              aria-hidden
              style={{
                marginTop: '1.55rem',
                width: '3.9rem',
                height: '.22rem',
                borderRadius: '9999px',
                backgroundColor: primaryColor,
              }}
            />
            <div
              style={{
                marginTop: '1.8rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.25rem',
                maxWidth: '31rem',
              }}
            >
              {currentSlide.features.map((feature) => (
                <div
                  key={feature.title}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '3.35rem 1fr',
                    gap: '.95rem',
                    alignItems: 'center',
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      width: '3.35rem',
                      height: '3.35rem',
                      borderRadius: '9999px',
                      border: `1px solid ${primaryColor}22`,
                      backgroundColor: 'rgba(255,255,255,.48)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: primaryColor,
                      boxShadow: '0 16px 38px rgba(37,99,235,.08)',
                    }}
                  >
                    {renderIcon(feature.icon)}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '1.05rem',
                        fontWeight: 800,
                        color: ink,
                        letterSpacing: '-.02em',
                      }}
                    >
                      {feature.title}
                    </div>
                    <div
                      style={{
                        marginTop: '.2rem',
                        fontSize: '.88rem',
                        lineHeight: 1.45,
                        color: muted,
                      }}
                    >
                      {feature.body}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div
              role="tablist"
              aria-label="Platform highlights"
              style={{ marginTop: '1.8rem', display: 'flex', alignItems: 'center', gap: '.55rem' }}
            >
              {slides.map((slide, index) => {
                const active = index === activeSlide;
                return (
                  <button
                    key={slide.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-label={`Show ${slide.eyebrow}`}
                    onClick={() => {
                      setActiveSlide(index);
                      setHasInteracted(true);
                    }}
                    style={{
                      width: active ? '2rem' : '.62rem',
                      height: '.62rem',
                      padding: 0,
                      border: 0,
                      borderRadius: '9999px',
                      backgroundColor: active ? primaryColor : `${primaryColor}45`,
                      cursor: 'pointer',
                      transition: prefersReducedMotion
                        ? 'none'
                        : 'width 220ms ease, background-color 220ms ease',
                    }}
                  />
                );
              })}
            </div>
          </div>
          <div
            style={{
              marginTop: 'auto',
              paddingTop: '2.5rem',
              fontSize: '.875rem',
              color: '#94a3b8',
            }}
          >
            Powered by Intelis and IPCA
          </div>
        </div>
      </div>
    </div>
  );
}
